import { create } from 'zustand';

export type TradeEntry = {
  price: string;
  qty: string;
  side: 'buy' | 'sell';
  ts: number;
  tradeId?: string;
};

const MAX_TRADES = 200;

type TradeState = {
  trades: Record<string, TradeEntry[]>;
  /** Set of tradeId keys we've already seen, per symbol. Prevents duplicates. */
  _seen: Record<string, Set<string>>;
  addTrade: (symbol: string, trade: TradeEntry) => void;
  setTrades: (symbol: string, trades: TradeEntry[]) => void;
  clear: (symbol: string) => void;
};

function tradeKey(t: TradeEntry): string {
  return t.tradeId ?? `${t.ts}-${t.price}-${t.qty}-${t.side}`;
}

export const useTradeStore = create<TradeState>()((set) => ({
  trades: {},
  _seen: {},

  addTrade: (symbol, trade) =>
    set((state) => {
      const key = tradeKey(trade);
      const seen = state._seen[symbol];
      if (seen?.has(key)) return state;

      const existing = state.trades[symbol] ?? [];
      const newSeen = new Set(seen);
      newSeen.add(key);
      // Cap the seen set to prevent unbounded growth
      if (newSeen.size > MAX_TRADES * 2) {
        const arr = [...newSeen];
        const trimmed = new Set(arr.slice(arr.length - MAX_TRADES));
        return {
          trades: {
            ...state.trades,
            [symbol]: [trade, ...existing.slice(0, MAX_TRADES - 1)],
          },
          _seen: { ...state._seen, [symbol]: trimmed },
        };
      }
      return {
        trades: {
          ...state.trades,
          [symbol]: [trade, ...existing.slice(0, MAX_TRADES - 1)],
        },
        _seen: { ...state._seen, [symbol]: newSeen },
      };
    }),

  setTrades: (symbol, trades) =>
    set((state) => {
      // Merge with existing _seen to avoid losing dedup tracking from live trades
      const existing = state._seen[symbol];
      const merged = new Set<string>(existing);
      // Deduplicate incoming trades against existing seen set
      const deduped: TradeEntry[] = [];
      for (const t of trades) {
        const key = tradeKey(t);
        if (!merged.has(key)) {
          deduped.push(t);
          merged.add(key);
        }
      }
      const capped = deduped.slice(0, MAX_TRADES);
      // Cap the seen set to prevent unbounded growth
      if (merged.size > MAX_TRADES * 2) {
        const arr = [...merged];
        const trimmed = new Set(arr.slice(arr.length - MAX_TRADES));
        return {
          trades: { ...state.trades, [symbol]: capped },
          _seen: { ...state._seen, [symbol]: trimmed },
        };
      }
      return {
        trades: { ...state.trades, [symbol]: capped },
        _seen: { ...state._seen, [symbol]: merged },
      };
    }),

  clear: (symbol) =>
    set((state) => {
      const { [symbol]: _t, ...restTrades } = state.trades;
      const { [symbol]: _s, ...restSeen } = state._seen;
      return { trades: restTrades, _seen: restSeen };
    }),
}));
